/**
 * Composable for managing focus trapping within panels or overlays.
 *
 * When active, traps Tab navigation within the container element.
 * Also provides utilities for storing/restoring focus and focusing
 * the first focusable element.
 *
 * @param containerRef - Reference to the container to trap focus within
 * @param isActive - Whether focus trapping is currently active
 * @returns Focus management utilities
 *
 * @example
 * const { storeFocus, restoreFocus } = useFocusManagement(panelRef, isPanelOpen)
 */

import { ref, watch, nextTick, onUnmounted, type Ref, type ComputedRef } from 'vue'

const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]:not([disabled])',
  '[role="menuitem"]:not([disabled])',
  '[role="option"]',
].join(', ')

export function useFocusManagement(
  containerRef: Ref<HTMLElement | null>,
  isActive: Ref<boolean> | ComputedRef<boolean>
) {
  const previousFocusElement = ref<HTMLElement | null>(null)
  let cleanupFn: (() => void) | null = null

  const storeFocus = () => {
    previousFocusElement.value = document.activeElement as HTMLElement
  }

  // Focus goes back where it was only if it is nowhere else: still in the container, or dropped on
  // the body because the container left the page. A reader who clicked elsewhere keeps their place.
  const restoreFocus = () => {
    const previous = previousFocusElement.value
    if (!previous || typeof previous.focus !== 'function') return
    previousFocusElement.value = null
    const container = containerRef.value
    nextTick(() => {
      const active = document.activeElement
      if (!active || active === document.body || container?.contains(active)) {
        previous.focus()
      }
    })
  }

  const focusElement = async (element: HTMLElement | Ref<HTMLElement | undefined>) => {
    await nextTick()
    const el = 'value' in element ? element.value : element
    if (el && typeof el.focus === 'function') {
      el.focus()
    }
  }

  const focusFirstElement = async (container?: HTMLElement | Ref<HTMLElement | undefined>) => {
    await nextTick()
    const target = container
      ? ('value' in container ? container.value : container)
      : containerRef.value
    if (!target) return

    const firstFocusable = target.querySelector(FOCUSABLE_SELECTORS) as HTMLElement
    if (firstFocusable) {
      firstFocusable.focus()
    }
  }

  function setupTrap(container: HTMLElement) {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const currentFocusableElements = container.querySelectorAll(FOCUSABLE_SELECTORS)
      if (currentFocusableElements.length === 0) return

      const currentFirstFocusable = currentFocusableElements[0] as HTMLElement
      const currentLastFocusable = currentFocusableElements[currentFocusableElements.length - 1] as HTMLElement

      const isWithinContainer = container.contains(document.activeElement as Node)

      if (!isWithinContainer) {
        event.preventDefault()
        if (event.shiftKey) {
          currentLastFocusable.focus()
        } else {
          currentFirstFocusable.focus()
        }
        return
      }

      if (event.shiftKey) {
        if (document.activeElement === currentFirstFocusable) {
          event.preventDefault()
          currentLastFocusable.focus()
        }
      } else {
        if (document.activeElement === currentLastFocusable) {
          event.preventDefault()
          currentFirstFocusable.focus()
        }
      }
    }

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement
      if (!container.contains(target)) {
        event.preventDefault()
        const focusableElements = container.querySelectorAll(FOCUSABLE_SELECTORS)
        const firstFocusable = focusableElements[0] as HTMLElement
        if (firstFocusable) {
          firstFocusable.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    document.addEventListener('focusin', handleFocusIn, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('focusin', handleFocusIn, true)
    }
  }

  watch([isActive, containerRef], ([active, container]) => {
    if (cleanupFn) {
      cleanupFn()
      cleanupFn = null
    }

    if (active && container) {
      storeFocus()
      cleanupFn = setupTrap(container)
      focusFirstElement()
    } else if (!active) {
      restoreFocus()
    }
  }, { immediate: true })

  // A container rendered with `v-if` is unmounted when it closes, and the watch above never sees
  // it go inactive.
  onUnmounted(() => {
    if (cleanupFn) {
      cleanupFn()
      cleanupFn = null
    }
    restoreFocus()
  })

  return {
    storeFocus,
    restoreFocus,
    focusElement,
    focusFirstElement,
    previousFocusElement,
  }
}
